import React, { forwardRef, ChangeEvent, Ref } from "react";
import FormField from "../formField/FormField";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { createMarkup, useBackgroundColor } from "../../../utils";

export interface CheckboxProps {
  label: string;
  id: string;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  invalid?: boolean;
  valid?: boolean;
}

interface StyledCheckboxProps {
  invalid?: boolean;
  valid?: boolean;
}

const StyledCheckbox = styled.div<StyledCheckboxProps>`
  ${({ theme: { space, colors, borderRadii }, invalid, valid }) => css`
    display: flex;
    gap: ${space.xSmall};

    label,
    input {
      cursor: pointer;
    }

    label {
      user-select: none;
      display: flex;
      align-items: flex-start;
      position: relative;
      gap: ${space.xSmall};
      color: ${valid ? colors.green : invalid ? colors.red : "currentColor"};

      &:before {
        content: "";
        width: 24px;
        height: 24px;
        border: solid 2px
          ${valid ? colors.green : invalid ? colors.red : colors.darkBlue};
        background: ${colors.white};
        transition: all 0.2s ease;
        border-radius: ${borderRadii.small};
        flex-shrink: 0;
      }

      &:after {
        content: "";
        width: 15.33px;
        height: 11.12px;
        background-image: url("data:image/svg+xml,%3Csvg width='16' height='12' viewBox='0 0 16 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.35481 11.6191C5.05269 11.6191 4.76255 11.4988 4.54879 11.2851L0.833895 7.5702C0.388701 7.125 0.388701 6.40334 0.833895 5.95815C1.27909 5.51295 2.00075 5.51295 2.44594 5.95815L5.35481 8.86702L13.888 0.833893C14.3331 0.388699 15.0548 0.388699 15.5 0.833893C15.9452 1.27909 15.9452 2.00075 15.5 2.44594L6.16083 11.2851C5.94707 11.4988 5.65692 11.6191 5.35481 11.6191Z' fill='currentColor'/%3E%3C/svg%3E%0A");
        position: absolute;
        top: 5px;
        left: 4px;
        opacity: 0;
        transform: scale(0);
        transition: all 0.2s ease;
      }

      transition: all 0.2s ease-in-out;

      // Hover

      &:hover {
        &:before {
          box-shadow: 0 0 0 1px ${colors.darkBlue};
          opacity: 0.9;
        }
      }
    }

    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;

      &:checked + label {
        &:before {
          background: ${colors.green};
        }

        &:after {
          opacity: 1;
          -webkit-transform: scale(1);
          transform: scale(1);
        }
      }

      &:focus-visible + label {
        &:before {
          outline: none;
          box-shadow: 0 0 0 4px ${colors.warningYellow};
        }
      }
    }
  `};
`;

export const Checkbox = forwardRef(
  (
    {
      label,
      id,
      checked,
      required,
      onChange,
      invalid,
      valid,
      ...rest
    }: CheckboxProps,
    ref: Ref<HTMLInputElement>
  ) => {
    return (
      <StyledCheckbox invalid={invalid} valid={valid}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          required={required}
          ref={ref}
          {...rest}
        />
        <label htmlFor={id}>{createMarkup(label)}</label>
      </StyledCheckbox>
    );
  }
);

export default Checkbox;
